import React, { useEffect, useRef, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";

const inCls = "text-sm border border-marine/20 rounded-sm p-2 bg-white focus:outline-none focus:border-marine/50 w-full";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const PHONE_RE = /^[0-9+\s().-]{6,20}$/;
const URL_RE = /^https?:\/\/[^\s]+\.[^\s]+$/i;

// Formatage clavier simple d'un numéro de téléphone, sans dépendance
// (aucune librairie de masque n'est présente dans le projet) : regroupe les
// chiffres par paires — format français local "06 12 34 56 78" par défaut,
// ou groupé après le "+" pour un numéro international.
export const formatPhoneInput = (raw) => {
  const hasPlus = raw.trim().startsWith("+");
  const digits = raw.replace(/\D/g, "").slice(0, 15);
  const grouped = digits.match(/.{1,2}/g)?.join(" ") || digits;
  return hasPlus ? `+${grouped}` : grouped;
};

const isEmpty = (v) => v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);

const validateAnswer = (q, val) => {
  const empty = isEmpty(val);
  if (empty) return q.required ? `« ${q.label} » est requis` : null;
  if (q.type === "email" && !EMAIL_RE.test(val)) return `« ${q.label} » : adresse e-mail invalide`;
  if (q.type === "telephone" && !PHONE_RE.test(val)) return `« ${q.label} » : numéro de téléphone invalide`;
  if (q.type === "lien" && !URL_RE.test(val)) return `« ${q.label} » : lien invalide (doit commencer par http:// ou https://)`;
  if (q.type === "choix_unique" && !(q.options || []).includes(val)) return `« ${q.label} » : option invalide`;
  if (q.type === "cases_a_cocher" && (!Array.isArray(val) || val.some((v) => !(q.options || []).includes(v)))) {
    return `« ${q.label} » : sélection invalide`;
  }
  return null;
};

const validateSection = (section, answers) => {
  for (const q of section.questions) {
    const err = validateAnswer(q, answers[q.id]);
    if (err) return err;
  }
  return null;
};

const QuestionField = ({ q, value, onChange, testId, inputRef }) => {
  const id = `form-q-${testId}-${q.id}`;
  // Même poids de police que les libellés de champ déjà en gras ailleurs
  // dans l'app (cf. le composant Field de ui-modal.js : font-semibold).
  const label = (
    <label htmlFor={id} className="block text-sm font-semibold text-marine mb-1">
      {q.label} {q.required && <span className="text-rouge" aria-hidden="true">*</span>}
      {q.required && <span className="sr-only"> (obligatoire)</span>}
    </label>
  );
  const desc = q.description ? <p className="text-xs text-marine/80 mb-1">{q.description}</p> : null;

  if (q.type === "reponse_longue") {
    return (<div>{label}{desc}<textarea id={id} ref={inputRef} value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} className={inCls} /></div>);
  }
  if (q.type === "telephone") {
    return (<div>{label}{desc}<input id={id} ref={inputRef} type="tel" inputMode="tel" value={value || ""}
      onChange={(e) => onChange(formatPhoneInput(e.target.value))} className={inCls} /></div>);
  }
  if (q.type === "email" || q.type === "lien") {
    const inputType = q.type === "email" ? "email" : "url";
    return (<div>{label}{desc}<input id={id} ref={inputRef} type={inputType} value={value || ""} onChange={(e) => onChange(e.target.value)} className={inCls} /></div>);
  }
  if (q.type === "choix_unique") {
    return (
      <fieldset>
        <legend className="block text-sm font-semibold text-marine mb-1">
          {q.label} {q.required && <span className="text-rouge" aria-hidden="true">*</span>}
        </legend>
        {desc}
        <div className="space-y-1.5">
          {(q.options || []).map((opt, oi) => (
            <label key={opt} className="flex items-center gap-2 text-sm text-marine cursor-pointer">
              <input ref={oi === 0 ? inputRef : undefined} type="radio" name={id} checked={value === opt} onChange={() => onChange(opt)} /> {opt}
            </label>
          ))}
        </div>
      </fieldset>
    );
  }
  if (q.type === "cases_a_cocher") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (opt) => onChange(arr.includes(opt) ? arr.filter((v) => v !== opt) : [...arr, opt]);
    return (
      <fieldset>
        <legend className="block text-sm font-semibold text-marine mb-1">
          {q.label} {q.required && <span className="text-rouge" aria-hidden="true">*</span>}
        </legend>
        {desc}
        <div className="space-y-1.5">
          {(q.options || []).map((opt, oi) => (
            <label key={opt} className="flex items-center gap-2 text-sm text-marine cursor-pointer">
              <input ref={oi === 0 ? inputRef : undefined} type="checkbox" checked={arr.includes(opt)} onChange={() => toggle(opt)} /> {opt}
            </label>
          ))}
        </div>
      </fieldset>
    );
  }
  // reponse_courte (par défaut)
  return (<div>{label}{desc}<input id={id} ref={inputRef} type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} className={inCls} /></div>);
};

const Stepper = ({ sections, current, maxReached, onJump, testId }) => (
  <ol className="flex items-center gap-1.5 mb-4" aria-label="Progression du formulaire">
    {sections.map((s, i) => {
      const done = i < current;
      const isCurrent = i === current;
      const clickable = i <= maxReached && !isCurrent;
      return (
        <React.Fragment key={s.id}>
          <li>
            <button type="button" onClick={() => clickable && onJump(i)} disabled={!clickable}
              data-testid={`form-step-${i}-${testId}`}
              aria-current={isCurrent ? "step" : undefined}
              aria-label={`Section ${i + 1} sur ${sections.length} : ${s.title}${done ? ", complétée" : ""}${isCurrent ? ", en cours" : ""}`}
              className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 transition-colors
                ${isCurrent ? "bg-secondary ring-2 ring-primary text-primary" : done ? "bg-primary text-white cursor-pointer" : clickable ? "bg-marine-50 text-marine cursor-pointer" : "bg-marine-50 text-muet cursor-not-allowed"}`}>
              {done ? <Icons.Check size={16} aria-hidden="true" /> : i + 1}
            </button>
          </li>
          {i < sections.length - 1 && <li aria-hidden="true" className={`flex-1 h-px min-w-[12px] ${i < current ? "bg-primary" : "bg-marine/15"}`} />}
        </React.Fragment>
      );
    })}
  </ol>
);

const isEmail = (t) => t === "email";

// Formulaire de phase côté client : remplissable en étapes (wizard, une
// section à la fois) tant que non soumis (status "pending"), ou verrouillé
// en lecture seule une fois complété (status "completed") — miroir de
// RdvCard/RdvEditor mais avec un seul composant couvrant les deux états.
export const FormCard = ({ form, onSubmit, testId = "etape", previewMode = false, onDirtyChange }) => {
  const [answers, setAnswers] = useState({});
  const [sectionIndex, setSectionIndex] = useState(0);
  const [maxReached, setMaxReached] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const [phase, setPhase] = useState("intro"); // "intro" | "form" | "done"
  const firstFieldRef = useRef(null);
  const introButtonRef = useRef(null);

  const sections = form?.sections || [];
  const currentSection = sections[sectionIndex];
  const isLast = sectionIndex === sections.length - 1;

  // Dérivé de sections/answers à chaque render — se met à jour en temps réel
  // sans état ni effet dédié.
  const totalQuestions = sections.reduce((n, s) => n + s.questions.length, 0);
  const filledQuestions = sections.reduce((n, s) => n + s.questions.filter((q) => !isEmpty(answers[q.id])).length, 0);
  const percent = totalQuestions > 0 ? Math.round((filledQuestions / totalQuestions) * 100) : 0;

  useEffect(() => {
    if (!currentSection) return;
    firstFieldRef.current?.focus();
    setAnnouncement(`Section ${sectionIndex + 1} sur ${sections.length} : ${currentSection.title}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionIndex]);

  useEffect(() => {
    const dirty = phase === "form" && Object.values(answers).some((v) => !isEmpty(v));
    onDirtyChange?.(dirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, phase]);

  useEffect(() => {
    if (phase !== "intro") return;
    // setTimeout(0) : s'exécute après le piège de focus du Modal parent
    // (useFocusTrap), qui focus par défaut le premier élément focusable du
    // panneau (la croix de fermeture) — on reprend volontairement la main
    // pour poser le focus initial sur le bouton "Commencer".
    const t = setTimeout(() => introButtonRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [phase]);

  if (!form) return null;

  const setVal = (qid, v) => setAnswers((a) => ({ ...a, [qid]: v }));

  const goNext = () => {
    const err = validateSection(currentSection, answers);
    if (err) return toast.error(err);
    setMaxReached((m) => Math.max(m, sectionIndex + 1));
    setSectionIndex((i) => i + 1);
  };
  const goBack = () => setSectionIndex((i) => Math.max(0, i - 1));
  const goTo = (i) => setSectionIndex(i);

  const submit = async () => {
    const err = validateSection(currentSection, answers);
    if (err) return toast.error(err);
    if (previewMode) {
      toast.success("Aperçu — formulaire non envoyé");
      setPhase("done");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { answers: Object.entries(answers).map(([question_id, value]) => ({ question_id, value })) };
      await onSubmit(payload);
      setPhase("done");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  if (phase === "done") {
    return (
      <div className="mt-3 border-t border-marine/10 pt-6 min-h-[240px] flex flex-col items-center justify-center text-center" data-testid={`form-done-${testId}`}>
        <Icons.CheckCircle2 size={16} className="text-marine mb-4" aria-hidden="true" />
        <p className="text-sm text-marine whitespace-pre-line">{form.outro?.message || "Merci, votre formulaire a bien été pris en compte."}</p>
      </div>
    );
  }

  if (form.status === "completed") {
    const byId = {};
    for (const a of form.answers || []) byId[a.question_id] = a.value;
    return (
      <div className="mt-3 rounded-sm border border-marine/15 bg-marine/[0.03] p-4" data-testid={`form-locked-${testId}`}>
        <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-marine/80 mb-3">
          <Icons.Lock size={16} className="text-marine" /> Formulaire complété
        </div>
        <div className="space-y-3">
          {sections.map((s) => (
            <div key={s.id}>
              <div className="text-sm font-semibold text-marine mb-1">{s.title}</div>
              <div className="space-y-1">
                {s.questions.map((q) => {
                  const val = byId[q.id];
                  const display = Array.isArray(val) ? (val.join(", ") || "—") : (val || "—");
                  return (
                    <div key={q.id} className="text-sm">
                      <span className="text-marine/80">{q.label} : </span>
                      <span className="text-marine font-medium">{display}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (phase === "intro") {
    return (
      <div className="mt-3 border-t border-marine/10 pt-6 min-h-[240px] flex flex-col items-center justify-center text-center" data-testid={`form-intro-${testId}`}>
        <Icons.ClipboardList size={16} className="text-marine mb-4" aria-hidden="true" />
        {form.intro?.title && <h3 className="text-base font-semibold text-marine mb-2">{form.intro.title}</h3>}
        {form.intro?.description && <p className="text-sm text-marine whitespace-pre-line mb-6">{form.intro.description}</p>}
        <button type="button" ref={introButtonRef} onClick={() => setPhase("form")} data-testid={`form-intro-start-${testId}`}
          className="bg-marine text-white rounded-sm px-4 py-2 text-sm hover:bg-marine-hover active:scale-95 transition-all">
          {form.intro?.button_label || "Commencer"}
        </button>
      </div>
    );
  }

  return (
    <div className="mt-3 border-t border-marine/10 pt-3" data-testid={`form-fill-${testId}`}>
      {previewMode && (
        <div className="mb-3 flex items-center gap-1.5 text-xs font-semibold text-bordeaux bg-bordeaux-50 border border-bordeaux/25 rounded-sm px-3 py-2" data-testid="form-preview-banner">
          <Icons.Eye size={16} aria-hidden="true" /> Aperçu — ce formulaire ne sera pas envoyé
        </div>
      )}
      <div className="text-xs uppercase tracking-wider text-marine/80 mb-2">Formulaire à compléter</div>
      <div aria-live="polite" className="sr-only">{announcement}</div>

      <div className="mb-3" data-testid={`form-progress-${testId}`}>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs uppercase tracking-wider text-marine/80">Progression</span>
          <span className="text-xs font-semibold text-primary">{percent}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
          <div className="h-full bg-primary transition-all" style={{ width: `${percent}%` }} />
        </div>
      </div>

      <Stepper sections={sections} current={sectionIndex} maxReached={maxReached} onJump={goTo} testId={testId} />

      {currentSection && (
        <div className="min-h-[240px]">
          <div className="text-sm font-semibold text-marine mb-0.5">{currentSection.title}</div>
          {currentSection.description && <p className="text-xs text-marine/80 mb-2">{currentSection.description}</p>}
          <div className="space-y-3">
            {currentSection.questions.map((q, qi) => (
              <QuestionField key={q.id} q={q} value={answers[q.id]} onChange={(v) => setVal(q.id, v)}
                testId={testId} inputRef={qi === 0 ? firstFieldRef : undefined} />
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mt-4">
        <button type="button" onClick={goBack} disabled={sectionIndex === 0} data-testid={`form-back-${testId}`}
          className="text-sm text-marine/80 border border-marine/25 rounded-sm px-3 py-2 hover:bg-marine/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
          Précédent
        </button>
        {isLast ? (
          <button onClick={submit} disabled={submitting} data-testid={`form-submit-${testId}`}
            className="bg-marine text-white rounded-sm px-4 py-2 text-sm hover:bg-marine-hover active:scale-95 transition-all disabled:opacity-50">
            Envoyer mes réponses
          </button>
        ) : (
          <button onClick={goNext} data-testid={`form-next-${testId}`}
            className="bg-marine text-white rounded-sm px-4 py-2 text-sm hover:bg-marine-hover active:scale-95 transition-all">
            Suivant
          </button>
        )}
      </div>
    </div>
  );
};

export default FormCard;
